import { useState, useEffect } from 'react';
import { Alert } from 'antd';
import { Modal, message, Button, Card, Typography, Input, Tooltip } from 'antd';
import { Form } from '@ant-design/compatible';
import moment from 'moment';
import './DetalhePedido.scss';
import ResumoDetalhePedido from './ResumoDetalhePedido';
import VendedorDetalhePedido from './VendedorDetalhePedido';
import ProdutoValoresDetalhePedido from './ProdutoValoresDetalhePedido';
import MapaPedido from './MapaPedido';
import { toLocaleMoney } from '../../../shared/services/NumberService';

import {
  accept as acceptService,
  reject,
  finish as finishService,
  cancel,
  getOrigem,
  getStatusText,
} from '../../../shared/services/http/PedidoService';
import ConfirmDelete from '../../../shared/components/confirmdelete/ConfirmDelete';
import {
  isBackOfficeOrUpgasEmployee,
  isResale,
} from '../../../shared/services/UserService';
import OrderStatusEnum from '../../../shared/Enum/OrderStatusEnum';
import TypeSalesmanEnum from '../../../shared/Enum/TypeSalesmanEnum';
import ConfirmVerificationCode from './ConfirmVerificationCode';
import MotivoCancelamentoRevendaEnum from '../../../shared/Enum/MotivoCancelamentoRevendaEnum';
import { withAppInsightsTracking } from '../../../shared/services/AppInsightsService';
import { find } from '../../../shared/services/http/PedidoService';
import DetalhePedidoSkeleton from './DetalhePedidoSkeleton';
import ReactPlaceholder from 'react-placeholder';
import MotoristaDetalhePedido from './MotoristaDetalhePedido';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Icon as IconifyIcon } from '@iconify/react';

// ---- Layout helpers for the history timeline ----
const FINALIZED_STATUSES = [
  OrderStatusEnum.FinalizadoPeloMotorista,
  OrderStatusEnum.FinalizadoPelaOrganizacao,
  OrderStatusEnum.FinalizadoPeloAdm,
  OrderStatusEnum.FinalizadoPeloFuncionarioUpgas,
  OrderStatusEnum.FinalizadoPeloCliente,
];

const CANCELED_OR_REJECTED_STATUSES_LIST = [
  OrderStatusEnum.RejeitadoPeloMotorista,
  OrderStatusEnum.RejeitadoPelaOrganizacao,
  OrderStatusEnum.CanceladoPeloMotorista,
  OrderStatusEnum.CanceladoPelaOrganizacao,
  OrderStatusEnum.CanceladoPeloCliente,
  OrderStatusEnum.CanceladoPorTempoExcedido,
  OrderStatusEnum.CanceladoPeloAdm,
  OrderStatusEnum.CanceladoPeloFuncionarioUpgas,
  OrderStatusEnum.RejeitadoPeloAdm,
  OrderStatusEnum.RejeitadoPeloFuncionarioUpgas,
];

const ACCEPTED_STATUSES_LIST = [
  OrderStatusEnum.AceitoPeloMotorista,
  OrderStatusEnum.AceitoPelaOrganizacao,
  OrderStatusEnum.AceitoPeloAdm,
  OrderStatusEnum.AceitoPeloFuncionarioUpgas,
];

const getStepIconName = (status) => {
  if (status === OrderStatusEnum.Solicitado) return 'fa6-solid:box';
  if (ACCEPTED_STATUSES_LIST.includes(status)) return 'fa6-solid:motorcycle';
  if (FINALIZED_STATUSES.includes(status)) return 'ph:check-fat-fill';
  if (CANCELED_OR_REJECTED_STATUSES_LIST.includes(status)) return 'ph:x';
  return 'ph:package-fill';
};

const getStepCircleColor = (status, isLast) => {
  if (!isLast) return '#b8b8b8';
  if (ACCEPTED_STATUSES_LIST.includes(status)) return '#f59e0b';
  if (FINALIZED_STATUSES.includes(status)) return '#6ac259';
  if (CANCELED_OR_REJECTED_STATUSES_LIST.includes(status)) return '#f05228';
  if (status === OrderStatusEnum.Solicitado) return '#006ec9';
  return '#b8b8b8';
};

const getStepLabelColor = (status, isLast) => {
  if (!isLast) return '#b8b8b8';
  if (ACCEPTED_STATUSES_LIST.includes(status)) return '#d97706';
  if (FINALIZED_STATUSES.includes(status)) return '#6ac259';
  if (CANCELED_OR_REJECTED_STATUSES_LIST.includes(status)) return '#f05228';
  if (status === OrderStatusEnum.Solicitado) return '#006ec9';
  return '#b8b8b8';
};

const getStatusBadgeClass = (statusId) => {
  if (FINALIZED_STATUSES.includes(statusId)) return 'status-green';
  if (CANCELED_OR_REJECTED_STATUSES_LIST.includes(statusId))
    return 'status-red';
  if (ACCEPTED_STATUSES_LIST.includes(statusId)) return 'status-em-andamento';
  if (statusId === OrderStatusEnum.Solicitado) return 'status-solicitado';
  return 'status-gray';
};

const buildHistorySteps = (historicoStatus) => {
  const steps = [];
  if (!Array.isArray(historicoStatus)) return steps;

  historicoStatus.forEach((h) => {
    steps.push({ ...h, isDeliveryStep: false });
    if (ACCEPTED_STATUSES_LIST.includes(h.status) && h.limiteEntrega) {
      steps.push({ ...h, isDeliveryStep: true });
    }
  });
  return steps;
};

const deliveryTimeOptions = [
  { value: '15 à 30 min', text: '15 à 30 min' },
  { value: '30 à 45 min', text: '30 à 45 min' },
  { value: '45 à 60 min', text: '45 à 60 min' },
  { value: '60 à 90 min', text: '60 à 90 min' },
];

const resaleCancelOptions = [
  {
    value: MotivoCancelamentoRevendaEnum.VamosFechar,
    text: 'Vamos fechar',
  },
  {
    value: MotivoCancelamentoRevendaEnum.ProblemasComVeiculos,
    text: 'Problemas com veículos',
  },
  {
    value: MotivoCancelamentoRevendaEnum.ForaDeArea,
    text: 'Fora de área, muito longe',
  },
  {
    value: MotivoCancelamentoRevendaEnum.AreaDeRisco,
    text: 'Área de risco',
  },
  {
    value: MotivoCancelamentoRevendaEnum.ClienteSolicitouCancelamento,
    text: 'Cliente solicitou o cancelamento',
  },
  {
    value: MotivoCancelamentoRevendaEnum.PedidoFoiTrote,
    text: 'O pedido foi trote',
  },
  {
    value: MotivoCancelamentoRevendaEnum.TelefoneClienteInvalido,
    text: 'Telefone do cliente inválido',
  },
  {
    value: MotivoCancelamentoRevendaEnum.EnderecoClienteInvalido,
    text: 'Endereço do cliente inválido',
  },
  {
    value: MotivoCancelamentoRevendaEnum.Outro,
    text: 'Outro motivo',
  },
];

export const isAccepted = (statusId) =>
  statusId === OrderStatusEnum.AceitoPeloMotorista ||
  statusId === OrderStatusEnum.AceitoPelaOrganizacao ||
  statusId === OrderStatusEnum.AceitoPeloAdm ||
  statusId === OrderStatusEnum.AceitoPeloFuncionarioUpgas;

const rejectedAndCanceledStatus = [
  OrderStatusEnum.RejeitadoPeloAdm,
  OrderStatusEnum.RejeitadoPeloFuncionarioUpgas,
  OrderStatusEnum.RejeitadoPelaOrganizacao,
  OrderStatusEnum.RejeitadoPeloMotorista,
  OrderStatusEnum.CanceladoPeloAdm,
  OrderStatusEnum.CanceladoPeloFuncionarioUpgas,
  OrderStatusEnum.CanceladoPelaOrganizacao,
  OrderStatusEnum.CanceladoPeloCliente,
];

const zoomMap = 11;

const BACKOFFICE_STATUSES = [
  OrderStatusEnum.AceitoPeloFuncionarioUpgas,
  OrderStatusEnum.FinalizadoPeloFuncionarioUpgas,
  OrderStatusEnum.AceitoPeloAdm,
  OrderStatusEnum.FinalizadoPeloAdm,
  OrderStatusEnum.CanceladoPeloAdm,
  OrderStatusEnum.CanceladoPeloFuncionarioUpgas,
  OrderStatusEnum.RejeitadoPeloAdm,
  OrderStatusEnum.RejeitadoPeloFuncionarioUpgas,
];

/**
 * Formats the status title with or without the backoffice user name
 *
 * @param {string} status - Status from history
 * @param {string} backofficeUserName - Backoffice user name
 * @returns {string} Formatted title
 */
const formatStatusTitle = (status, backofficeUserName) => {
  const baseTitle = getStatusText(status);
  const shouldShowUserName =
    backofficeUserName && BACKOFFICE_STATUSES.includes(status);

  return shouldShowUserName
    ? `${baseTitle} (${backofficeUserName})`
    : baseTitle;
};

/**
 * Represents a component for displaying detailed information about a pedido (order).
 *
 * @param {Object} props - The props object containing the following properties:
 *   - isOpen: A boolean indicating whether the modal is open or not.
 *   - orderId: The ID of the order.
 *   - form: The form object for form validation.
 *   - onClose: A function to be called when the modal is closed.
 * @returns {JSX.Element} The JSX element representing the DetalhePedido component.
 */
function DetalhePedido({ isOpen, orderId, form, onClose }) {
  const [order, setOrder] = useState({});
  const [loading, setLoading] = useState(false);
  const [rejectingOrCanceling, setRejectingOrCanceling] = useState(false);
  const [selectedCancelOption, setSelectedCancelOption] = useState(null);
  const [reasonForCancellationOthers, setReasonForCancellationOthers] =
    useState('');
  const [cancelarPorOrganizacao, setCancelarPorOrganizacao] = useState(false);
  const [showTheMap, setShowTheMap] = useState(false);
  const [modalWidth, setModalWidth] = useState(1024);
  const [canShowSensitiveData, setCanShowSensitiveData] = useState(
    isBackOfficeOrUpgasEmployee(),
  );
  const [selectedDeliveryTime, setSelectedDeliveryTime] = useState(null);

  const toggleLoading = (callBack) => {
    setLoading((prevState) => !prevState);
    callBack && callBack();
  };

  const toggleErrorLoading = (callBack) => {
    callBack && callBack();
  };

  useEffect(() => {
    if (isOpen) {
      toggleLoading();
      find(orderId)
        .then((order) => {
          setOrder(order);
          const isAdmin = isBackOfficeOrUpgasEmployee();
          if (!isAdmin) {
            setCanShowSensitiveData(isAccepted(order.statusId));
          }
        })
        .catch((error) => message.error(error))
        .finally(() => toggleLoading());
    }

    const updateWidth = () => {
      setModalWidth(window.innerWidth <= 1100 ? window.innerWidth : 1024);
    };
    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => window.removeEventListener('resize', updateWidth);
  }, [isOpen, orderId]);

  const beforeClose = () => {
    setRejectingOrCanceling(false);
    setSelectedCancelOption(null);
    setCancelarPorOrganizacao(false);
    setSelectedDeliveryTime(null);
    onClose();
  };

  const accept = async (values, deliveryTime) => {
    try {
      await acceptService(values, deliveryTime);
      toggleLoading(() => {
        beforeClose();
        message.success('Pedido em andamento!');
      });
    } catch (error) {
      toggleLoading(() => message.error(error));
    }
  };

  const orderReject = async () => {
    toggleErrorLoading(async () => {
      try {
        await reject({
          ...order,
          motivoCancelamentoRevenda: selectedCancelOption,
          motivo: reasonForCancellationOthers,
        });
        toggleErrorLoading(() => {
          beforeClose();
          message.success('Pedido rejeitado com sucesso!');
        });
      } catch (error) {
        toggleErrorLoading(() => message.error(error));
      }
    });
  };

  const orderCanceled = async () => {
    toggleErrorLoading(async () => {
      try {
        await cancel({
          ...order,
          motivoCancelamentoRevenda: selectedCancelOption,
          motivo: reasonForCancellationOthers,
          cancelarPorOrganizacao,
        });
        toggleErrorLoading(() => {
          beforeClose();
          message.success('Pedido cancelado com sucesso!');
        });
      } catch (error) {
        toggleErrorLoading(() => message.error(error));
      }
    });
  };

  const finish = async (code = null, withCodeConfirmation) => {
    toggleLoading(async () => {
      try {
        if (
          withCodeConfirmation &&
          order.codigoConfirmacao &&
          order.codigoConfirmacao !== code
        ) {
          toggleLoading(() => message.error('Código de verificação inválido'));
          return;
        }

        await finishService(order, withCodeConfirmation);

        toggleLoading(() => {
          beforeClose();
          message.success('Pedido finalizado com sucesso!');
        });
      } catch (error) {
        toggleLoading(() => message.error(error));
      }
    });
  };

  const handleSubmit = () => {
    toggleLoading(() => accept(order, selectedDeliveryTime));
  };

  const handleRejectCancel = () => {
    order.statusId === OrderStatusEnum.Solicitado
      ? orderReject()
      : orderCanceled();
  };

  const valorProdutos =
    Array.isArray(order.products) &&
    order.products.reduce((acc, curr) => acc + curr.valor * curr.quantidade, 0);

  const modalFooter = rejectingOrCanceling ? (
    <div className='dp-modal__footer'>
      <button
        type='button'
        className='dp-modal__btn dp-modal__btn--cancel'
        onClick={() => setRejectingOrCanceling(false)}>
        Cancelar
      </button>
      <button
        type='button'
        className='dp-modal__btn dp-modal__btn--primary'
        disabled={
          selectedCancelOption === null ||
          (selectedCancelOption === MotivoCancelamentoRevendaEnum.Outro &&
            !reasonForCancellationOthers.trim())
        }
        onClick={() => handleRejectCancel()}>
        Enviar
      </button>
    </div>
  ) : (
    <div className={`dp-modal__footer${order.statusId === OrderStatusEnum.Solicitado && order.tipoPedido === TypeSalesmanEnum.Resale ? ' dp-modal__footer--with-delivery' : ''}`}>
      {order.statusId === OrderStatusEnum.Solicitado && (
        <>
          {order.tipoPedido === TypeSalesmanEnum.Resale && (
            <div className='dp-delivery-time'>
              <span className='dp-delivery-time__label'>Tempo de entrega</span>
              <div className='dp-delivery-time__chips'>
                {deliveryTimeOptions.map(({ value, text }) => (
                  <button
                    key={value}
                    type='button'
                    className={`dp-delivery-time__chip${selectedDeliveryTime === value ? ' dp-delivery-time__chip--selected' : ''}`}
                    onClick={() => setSelectedDeliveryTime(value)}>
                    {text}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className='dp-modal-footer-actions'>
            {(isBackOfficeOrUpgasEmployee() || isResale()) && (
              <button
                type='button'
                className='dp-modal__btn dp-modal__btn--cancel'
                onClick={() => setRejectingOrCanceling(true)}>
                Rejeitar pedido
              </button>
            )}
            <button
              type='button'
              className='dp-modal__btn dp-modal__btn--primary'
              disabled={
                order.tipoPedido === TypeSalesmanEnum.Resale &&
                !selectedDeliveryTime
              }
              onClick={() => handleSubmit()}>
              Aceitar pedido
            </button>
          </div>
        </>
      )}
      {isAccepted(order.statusId) && (
        <>
          <Tooltip
            placement='top'>
            <span>
              <button
                type='button'
                className='dp-modal__btn dp-modal__btn--cancel'
                onClick={() =>setRejectingOrCanceling(true)}>
                Cancelar pedido
              </button>
            </span>
          </Tooltip>
          <Button
            type='primary'
            loading={loading}
            className='dp-modal__btn dp-modal__btn--primary'
            onClick={() =>
              order.codigoConfirmacao
                ? ConfirmVerificationCode(finish)
                : ConfirmDelete(finish, {
                  title: 'Deseja finalizar?',
                  message: 'Você realmente deseja finalizar esse pedido?',
                  confirmText: 'Finalizar',
                })
            }>
            Finalizar etapa
          </Button>
        </>
      )}
      {order.statusId !== undefined &&
        !isAccepted(order.statusId) &&
        order.statusId !== OrderStatusEnum.Solicitado && (
        <button
          type='button'
          className='dp-modal__btn dp-modal__btn--primary'
          onClick={beforeClose}>
            Fechar
        </button>
      )}
    </div>
  );

  return (
    <Modal
      title={
        <div className='dp-header'>
          <div className='dp-header-left'>
            <div className='dp-header-icon'>
              <IconifyIcon
                icon='fa6-solid:basket-shopping'
                style={{ width: 17, height: 17, color: '#3193ff' }}
              />
            </div>
            <div className='dp-header-info'>
              <span className='dp-header-label'>Pedido</span>
              <span className='dp-header-number'>{order.protocol}</span>
            </div>
          </div>

          <div className='dp-header-right'>
            <div className='dp-header-badges'>
              {order.suspectedFraud && (
                <span className='dp-header-fraud'>
                  <ExclamationCircleOutlined />
                  Suspeita de fraude!
                </span>
              )}

              {isBackOfficeOrUpgasEmployee() && order.origem && (
                <span
                  className='dp-header-badge status-gray'
                  data-testid='origem-pedido'>
                  {getOrigem(order.origem)}
                </span>
              )}

              {order.status && (
                <span
                  className={`dp-header-badge ${getStatusBadgeClass(order.statusId)}`}>
                  <IconifyIcon
                    icon='ph:circle-fill'
                    style={{ width: 8, height: 8 }}
                  />
                  {order.status}
                </span>
              )}

              {order.cancelReason &&
                CANCELED_OR_REJECTED_STATUSES_LIST.includes(order.statusId) && (
                <span className='dp-header-badge status-motivo'>
                  {`Motivo: ${order.cancelReason}`}
                </span>
              )}

              {order.averageDeliveryTime && (
                <span className='dp-header-badge status-blue'>
                  <IconifyIcon
                    icon='ph:clock'
                    style={{ width: 16, height: 16 }}
                  />
                  {`Tempo Médio: ${Math.floor(moment.duration(order.averageDeliveryTime).asHours())}h ${moment.duration(order.averageDeliveryTime).minutes()}min`}
                </span>
              )}
            </div>
            <button
              type='button'
              className='dp-header-close'
              onClick={beforeClose}
              aria-label='Fechar'>
              <IconifyIcon
                icon='fa6-solid:xmark'
                style={{ width: 20, height: 20 }}
              />
            </button>
          </div>
        </div>
      }
      width={modalWidth}
      onCancel={beforeClose}
      visible={isOpen}
      closable={false}
      footer={modalFooter}
      centered
      destroyOnClose
      className='dp-modal'>
      <ReactPlaceholder
        showLoadingAnimation={true}
        ready={!loading}
        customPlaceholder={<DetalhePedidoSkeleton />}>
        {!rejectingOrCanceling ? (
          <>
            {showTheMap && (
              <MapaPedido
                localizacaoVendedor={
                  order.salesMan
                    ? order.salesMan.localizacao
                    : { latitude: 0, longitude: 0 }
                }
                localizacaoCliente={
                  order.customer
                    ? order.customer.localizacao
                    : { latitude: 0, longitude: 0 }
                }
                tipoPedido={order.tipoPedido}
                zoom={zoomMap}
              />
            )}

            <Form
              className='order-detail'
              layout='vertical'
              onSubmit={handleSubmit}>
              <Form.Item>
                {order.statusId === OrderStatusEnum.Solicitado &&
                  order.customer?.hasRecentCancellation && (
                  <Alert
                    message='Atenção: Este cliente teve um pedido cancelado recentemente.'
                    description='Fique atento ao analisar este pedido.'
                    type='warning'
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {Array.isArray(order.historicoStatus) &&
                  order.historicoStatus.length > 0 && (
                  <div className='dp-section'>
                    <h2 className='dp-section-title'>Histórico do Pedido</h2>
                    <div className='dp-timeline'>
                      {buildHistorySteps(order.historicoStatus).map(
                        (step, index, steps) => {
                          const lastRealIndex = steps.reduce(
                            (acc, s, i) => (!s.isDeliveryStep ? i : acc),
                            -1,
                          );
                          const isLast =
                            !step.isDeliveryStep && index === lastRealIndex;
                          const circleColor = step.isDeliveryStep
                            ? '#b8b8b8'
                            : getStepCircleColor(step.status, isLast);
                          const labelColor = step.isDeliveryStep
                            ? '#b8b8b8'
                            : getStepLabelColor(step.status, isLast);
                          const iconName = step.isDeliveryStep
                            ? 'ph:package-fill'
                            : getStepIconName(step.status);
                          const label = step.isDeliveryStep
                            ? 'Entrega até:'
                            : formatStatusTitle(
                              step.status,
                              step.backofficeUserName,
                            );
                          const dateText = step.isDeliveryStep
                            ? moment(step.limiteEntrega).format('HH:mm')
                            : ACCEPTED_STATUSES_LIST.includes(step.status)
                              ? `Aceito às ${moment(step.data).format('HH:mm')}`
                              : moment(step.data).format('DD/MM/YYYY HH:mm');

                          return (
                            <div key={index} style={{ display: 'contents' }}>
                              <div className='dp-timeline-step'>
                                <div
                                  className='dp-timeline-step-circle'
                                  style={{ background: circleColor }}>
                                  <IconifyIcon
                                    icon={iconName}
                                    style={{
                                      width: 16,
                                      height: 16,
                                      color: 'white',
                                    }}
                                  />
                                </div>
                                <div className='dp-timeline-step-info'>
                                  <span
                                    className='dp-timeline-step-label'
                                    style={{ color: labelColor }}>
                                    {label}
                                  </span>
                                  <span className='dp-timeline-step-date'>
                                    {dateText}
                                  </span>
                                </div>
                              </div>
                              {index < steps.length - 1 && (
                                <div className='dp-timeline-connector' />
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>
                    {rejectedAndCanceledStatus.some(
                      (status) => status === order.statusId,
                    ) &&
                      order.cancelReason === 'Outro motivo' && (
                      <Card
                        className='order-cancel-reason-description'
                        style={{ marginTop: 16 }}>
                        <small>
                          <b>Motivo cancelamento:</b>
                        </small>
                        <Typography.Paragraph className='description'>
                          {order.cancelReasonDescription}
                        </Typography.Paragraph>
                      </Card>
                    )}
                  </div>
                )}

                {order.customer && (
                  <div className='dp-section'>
                    <h2 className='dp-section-title'>Resumo do Pedido</h2>
                    <div className='dp-info-cards'>
                      <ResumoDetalhePedido
                        order={order}
                        canShowSensitiveData={canShowSensitiveData}
                        onShowMap={() => setShowTheMap(true)}
                      />
                      {(order.salesMan || order.resaleName) && (
                        <VendedorDetalhePedido order={order} />
                      )}
                    </div>
                    {order.driverId && (
                      <div style={{ marginTop: 16 }}>
                        <MotoristaDetalhePedido order={order} />
                      </div>
                    )}
                  </div>
                )}

                <div className='dp-section'>
                  <h2 className='dp-section-title'>Produto e Valores</h2>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16,
                    }}>
                    <ProdutoValoresDetalhePedido order={order} />

                    <div className='dp-values-card'>
                      <div className='dp-values-card-rows'>
                        <div className='dp-values-card-row'>
                          <span className='dp-values-card-row-label'>
                            Valor
                          </span>
                          <span className='dp-values-card-row-value'>
                            {valorProdutos != null
                              ? toLocaleMoney(valorProdutos)
                              : '—'}
                          </span>
                        </div>
                        <div className='dp-values-card-row'>
                          <span className='dp-values-card-row-label'>
                            Desconto
                          </span>
                          <span className='dp-values-card-row-value green'>
                            {`- ${order.discount || 'R$ 0,00'}`}
                          </span>
                        </div>
                        <div className='dp-values-card-row'>
                          <span className='dp-values-card-row-label'>
                            Taxa do cliente
                          </span>
                          <span className='dp-values-card-row-value'>
                            {order.taxaServico}
                          </span>
                        </div>
                        <div className='dp-values-card-row'>
                          <span className='dp-values-card-row-label'>
                            Cupom
                          </span>
                          <span className='dp-values-card-row-value green'>
                            {`- ${order.coupon?.discount ?? order.descontoIndiqueGanhe ?? 'R$ 0,00'}`}
                          </span>
                        </div>
                        <div className='dp-values-card-row total'>
                          <span className='dp-values-card-row-label'>
                            Valor Total
                          </span>
                          <span className='dp-values-card-row-value'>
                            {order.priceWithDiscount}
                          </span>
                        </div>
                      </div>

                      <div className='dp-values-card-divider'>
                        <div className='dp-values-card-payment'>
                          <div className='dp-values-card-payment-method'>
                            <IconifyIcon
                              icon='fa6-solid:credit-card'
                              style={{
                                width: 16,
                                height: 16,
                                color: '#595959',
                              }}
                            />
                            <span className='dp-values-card-payment-method-label'>
                              Forma de pagamento:
                            </span>
                            <span className='dp-values-card-payment-method-value'>
                              {order.paymentType}
                            </span>
                          </div>
                          <div className='dp-values-card-payment-change'>
                            <span className='dp-values-card-payment-change-label'>
                              Troco:
                            </span>
                            <span className='dp-values-card-payment-change-value'>
                              {order.change}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </Form.Item>
            </Form>
          </>
        ) : (
          <>
            <h1>Motivo de Rejeição/Cancelamento</h1>
            {resaleCancelOptions.map(({ value, text }) => (
              <Button
                className='mb-10'
                block
                onClick={() => setSelectedCancelOption(value)}
                type={value === selectedCancelOption ? 'primary' : 'default'}>
                {text}
              </Button>
            ))}

            {selectedCancelOption === MotivoCancelamentoRevendaEnum.Outro && (
              <Input.TextArea
                rows={4}
                placeholder='Descreva o motivo do cancelamento (Obrigatório)'
                onChange={({ target }) =>
                  setReasonForCancellationOthers(target.value)
                }
              />
            )}

            {isBackOfficeOrUpgasEmployee() &&
              order.statusId !== OrderStatusEnum.Solicitado && (
              <div className='mt-10'>
                <input
                  type='checkbox'
                  id='cancelarPorOrganizacao'
                  checked={cancelarPorOrganizacao}
                  onChange={(e) =>
                    setCancelarPorOrganizacao(e.target.checked)
                  }
                  style={{ marginRight: '8px' }}
                />
                <label htmlFor='cancelarPorOrganizacao'>
                    Cancelar por organização
                </label>
              </div>
            )}
          </>
        )}
      </ReactPlaceholder>
    </Modal>
  );
}

export default withAppInsightsTracking(Form.create()(DetalhePedido));
